import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { memo, useEffect, useMemo, useState } from "react";
import { getLinkTitle, getLinkDescription } from "../helpers";
import { getDomain } from "@shared/blocks/helpers";
import "./view.scss";
const RESERVED_PROFILE_SEGMENTS = new Set([
    "about",
    "account",
    "codespaces",
    "contact",
    "customer-stories",
    "enterprise",
    "events",
    "explore",
    "features",
    "issues",
    "login",
    "marketplace",
    "new",
    "notifications",
    "orgs",
    "organizations",
    "pricing",
    "pulls",
    "search",
    "security",
    "settings",
    "site",
    "sponsors",
    "teams",
    "topics",
    "trending",
]);
export const GitHubBlockView = memo(({ block }) => {
    const content = block.content;
    const url = content.url;
    const target = useMemo(() => parseGitHubTarget(url), [url]);
    const profileLogin = target.kind === "profile" ? target.owner : undefined;
    const repoLabel = target.kind === "profile" && profileLogin
        ? `@${profileLogin}`
        : target.owner && target.repo
            ? `${target.owner}/${target.repo}`
            : getDomain(url) || "github.com";
    const meta = useGitHubMeta(target);
    const title = stripGitHubSuffix(getLinkTitle(content) || block.title || "");
    const description = getLinkDescription(content) || block.summary || "";
    const headline = meta?.title || title || repoLabel;
    const source = url ? (_jsx("a", { href: url, target: "_blank", rel: "noopener noreferrer", className: "github-content__source", onClick: (e) => e.stopPropagation(), onMouseDown: (e) => e.stopPropagation(), children: repoLabel })) : (_jsx("div", { className: "github-content__source", children: repoLabel }));
    const diffMeta = formatDiff(meta?.additions, meta?.deletions);
    const repoMetaAvailable = typeof meta?.stars === "number" ||
        typeof meta?.forks === "number" ||
        typeof meta?.openIssues === "number" ||
        typeof meta?.repoSizeKb === "number";
    const profileMetaAvailable = typeof meta?.followers === "number" ||
        typeof meta?.following === "number" ||
        typeof meta?.publicRepos === "number";
    const commitMetaAvailable = typeof meta?.changedFiles === "number" ||
        typeof meta?.additions === "number" ||
        typeof meta?.deletions === "number" ||
        Boolean(meta?.authorName) ||
        Boolean(meta?.authoredAt);
    const shouldShowMetaRow = target.kind === "pull" ||
        target.kind === "issue" ||
        (target.kind === "repo" && repoMetaAvailable) ||
        (target.kind === "profile" && profileMetaAvailable) ||
        (target.kind === "commit" && commitMetaAvailable);
    const profileHeadline = meta?.profileName || headline;
    const renderedDescription = target.kind === "profile" ? meta?.bio || description : description;
    const commitDateLabel = meta?.authoredAt ? formatCommitDate(meta.authoredAt) : undefined;
    return (_jsxs("div", { className: `github-content github-content--${target.kind}`, children: [_jsxs("div", { className: "github-content__header", children: [source, meta?.avatarUrl ? (_jsx("img", { className: "github-content__avatar", src: meta.avatarUrl, alt: "", width: 24, height: 24 })) : null] }), _jsx("div", { className: "github-content__title", children: target.kind === "profile" ? profileHeadline : headline }), renderedDescription ? _jsx("div", { className: "github-content__description", children: renderedDescription }) : null, shouldShowMetaRow ? (_jsx("div", { className: "github-content__meta-row", children: buildMetaRowItems(target, meta, diffMeta, commitDateLabel) })) : null] }));
});
function useGitHubMeta(_target) {
    // DISABLED NETWORK FETCH: this hook was calling api.github.com on every
    // mount to hydrate stars/forks/issues/etc. Disabled to keep initial load
    // snappy and to avoid burning the 60 req/hr unauthenticated rate limit.
    // Re-enable by restoring the block below and moving enrichment to the
    // ingestion pipeline (persist into block.content) or gating on viewport.
    //
    // const [meta, setMeta] = useState<GitHubMeta>();
    //
    // useEffect(() => {
    //   setMeta(undefined);
    //   let cancelled = false;
    //
    //   const hydrate = async (): Promise<void> => {
    //     try {
    //       const endpoint = resolveGitHubEndpoint(target);
    //       if (!endpoint) return;
    //       const res = await fetch(endpoint, {
    //         headers: {
    //           Accept: "application/vnd.github+json",
    //           "X-GitHub-Api-Version": "2022-11-28",
    //         },
    //       });
    //       if (!res.ok || cancelled) return;
    //       const data = (await res.json()) as Record<string, unknown>;
    //       if (cancelled) return;
    //
    //       setMeta(mapGitHubMeta(target, data));
    //     } catch {
    //       // best-effort enrichment only
    //     }
    //   };
    //
    //   void hydrate();
    //   return () => {
    //     cancelled = true;
    //   };
    // }, [target.kind, target.owner, target.repo, target.number, target.sha]);
    return undefined;
}
function parseGitHubTarget(url) {
    if (!url)
        return { kind: "other" };
    try {
        const parsed = new URL(url);
        const host = parsed.hostname.toLowerCase();
        if (host !== "github.com" && host !== "www.github.com")
            return { kind: "other" };
        const segments = parsed.pathname.split("/").filter(Boolean);
        if (segments.length === 1) {
            const maybeProfile = segments[0]?.toLowerCase();
            if (maybeProfile && RESERVED_PROFILE_SEGMENTS.has(maybeProfile) === false) {
                return { kind: "profile", owner: segments[0] };
            }
            return { kind: "other" };
        }
        if (segments.length < 2)
            return { kind: "other" };
        const [owner, repo, section, number] = segments;
        if (!owner || !repo)
            return { kind: "other" };
        if (section === "commit" && number) {
            return { kind: "commit", owner, repo, sha: number };
        }
        if (section === "pull" && number && Number.isFinite(Number(number))) {
            return { kind: "pull", owner, repo, number: Number(number) };
        }
        if (section === "issues" && number && Number.isFinite(Number(number))) {
            return { kind: "issue", owner, repo, number: Number(number) };
        }
        if (!section)
            return { kind: "repo", owner, repo };
        return { kind: "other", owner, repo };
    }
    catch {
        return { kind: "other" };
    }
}
function resolveGitHubEndpoint(target) {
    if (target.kind === "profile" && target.owner) {
        return `https://api.github.com/users/${encodeURIComponent(target.owner)}`;
    }
    if (!target.owner || !target.repo)
        return undefined;
    const basePath = `https://api.github.com/repos/${encodeURIComponent(target.owner)}/${encodeURIComponent(target.repo)}`;
    if (target.kind === "pull" && target.number) {
        return `${basePath}/pulls/${target.number}`;
    }
    if (target.kind === "issue" && target.number) {
        return `${basePath}/issues/${target.number}`;
    }
    if (target.kind === "commit" && target.sha) {
        return `${basePath}/commits/${encodeURIComponent(target.sha)}`;
    }
    return basePath;
}
function mapGitHubMeta(target, data) {
    const userAvatar = asString(data.user?.avatar_url);
    const ownerAvatar = asString(data.owner?.avatar_url);
    const authorAvatar = asString(data.author?.avatar_url);
    const committerAvatar = asString(data.committer?.avatar_url);
    const commitData = data.commit;
    const commitAuthor = commitData?.author;
    const commitMessage = asString(commitData?.message);
    const commitFiles = Array.isArray(data.files) ? data.files.length : undefined;
    const commitStats = data.stats;
    return {
        title: target.kind === "profile"
            ? asString(data.login)
            : target.kind === "commit"
                ? getCommitSubject(commitMessage) ?? asString(data.sha)
                : asString(data.title),
        comments: asNumber(data.comments),
        additions: target.kind === "commit"
            ? asNumber(commitStats?.additions)
            : asNumber(data.additions),
        deletions: target.kind === "commit"
            ? asNumber(commitStats?.deletions)
            : asNumber(data.deletions),
        changedFiles: target.kind === "commit"
            ? commitFiles
            : asNumber(data.changed_files),
        avatarUrl: target.kind === "profile"
            ? asString(data.avatar_url)
            : target.kind === "commit"
                ? authorAvatar ?? committerAvatar ?? userAvatar ?? ownerAvatar
                : userAvatar ?? ownerAvatar,
        stars: asNumber(data.stargazers_count),
        forks: asNumber(data.forks_count),
        openIssues: asNumber(data.open_issues_count),
        repoSizeKb: asNumber(data.size),
        profileName: asString(data.name),
        bio: asString(data.bio),
        followers: asNumber(data.followers),
        following: asNumber(data.following),
        publicRepos: asNumber(data.public_repos),
        authorName: asString(data.author?.login) ?? asString(commitAuthor?.name),
        authoredAt: asString(data.author?.date) ?? asString(commitAuthor?.date),
    };
}
function buildMetaRowItems(target, meta, diffMeta, commitDateLabel) {
    const items = [];
    if ((target.kind === "pull" || target.kind === "issue") && target.number) {
        items.push(_jsxs("span", { className: "github-content__pill", children: [target.kind === "pull" ? "PR" : "Issue", "#", target.number] }, "pill"));
    }
    if (typeof meta?.comments === "number") {
        items.push(_jsxs("span", { className: "github-content__meta-item", children: [meta.comments, " comments"] }, "comments"));
    }
    if (target.kind === "pull" && typeof meta?.changedFiles === "number") {
        items.push(_jsxs("span", { className: "github-content__meta-item", children: [meta.changedFiles, " files"] }, "pull-files"));
    }
    if (target.kind === "pull" && diffMeta) {
        items.push(_jsx("span", { className: "github-content__diff", children: diffMeta }, "pull-diff"));
    }
    if (target.kind === "commit" && typeof meta?.changedFiles === "number") {
        items.push(_jsxs("span", { className: "github-content__meta-item", children: [meta.changedFiles, " files"] }, "commit-files"));
    }
    if (target.kind === "commit" && diffMeta) {
        items.push(_jsx("span", { className: "github-content__diff", children: diffMeta }, "commit-diff"));
    }
    if (target.kind === "commit" && meta?.authorName) {
        items.push(_jsxs("span", { className: "github-content__meta-item", children: ["by ", meta.authorName] }, "commit-author"));
    }
    if (target.kind === "commit" && commitDateLabel) {
        items.push(_jsx("span", { className: "github-content__meta-item", children: commitDateLabel }, "commit-date"));
    }
    if (target.kind === "profile" && typeof meta?.followers === "number") {
        items.push(_jsxs("span", { className: "github-content__meta-item", children: [formatCompact(meta.followers), " followers"] }, "profile-followers"));
    }
    if (target.kind === "profile" && typeof meta?.following === "number") {
        items.push(_jsxs("span", { className: "github-content__meta-item", children: [formatCompact(meta.following), " following"] }, "profile-following"));
    }
    if (target.kind === "profile" && typeof meta?.publicRepos === "number") {
        items.push(_jsxs("span", { className: "github-content__meta-item", children: [formatCompact(meta.publicRepos), " repos"] }, "profile-repos"));
    }
    if (target.kind === "repo" && typeof meta?.stars === "number") {
        items.push(_jsxs("span", { className: "github-content__meta-item", children: [formatCompact(meta.stars), " stars"] }, "repo-stars"));
    }
    if (target.kind === "repo" && typeof meta?.forks === "number") {
        items.push(_jsxs("span", { className: "github-content__meta-item", children: [formatCompact(meta.forks), " forks"] }, "repo-forks"));
    }
    if (target.kind === "repo" && typeof meta?.openIssues === "number") {
        items.push(_jsxs("span", { className: "github-content__meta-item", children: [formatCompact(meta.openIssues), " open"] }, "repo-open"));
    }
    if (target.kind === "repo" && typeof meta?.repoSizeKb === "number") {
        items.push(_jsx("span", { className: "github-content__meta-item", children: formatRepoSize(meta.repoSizeKb) }, "repo-size"));
    }
    return items;
}
function stripGitHubSuffix(text) {
    if (!text)
        return "";
    return text
        .replace(/\s*·\s*GitHub\s*$/i, "")
        .replace(/\s*·\s*Pull Request\s*#\d+\s*$/i, "")
        .trim();
}
function asString(value) {
    return typeof value === "string" ? value : undefined;
}
function asNumber(value) {
    return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}
function formatDiff(additions, deletions) {
    const hasAdditions = typeof additions === "number" && additions >= 0;
    const hasDeletions = typeof deletions === "number" && deletions >= 0;
    if (!hasAdditions && !hasDeletions)
        return undefined;
    return `+${additions ?? 0} -${deletions ?? 0}`;
}
function formatCompact(value) {
    if (value >= 1_000_000)
        return `${(value / 1_000_000).toFixed(1)}m`;
    if (value >= 1_000)
        return `${(value / 1_000).toFixed(1)}k`;
    return String(value);
}
function formatRepoSize(sizeKb) {
    if (sizeKb >= 1024) {
        return `${(sizeKb / 1024).toFixed(1)} MB`;
    }
    return `${sizeKb} KB`;
}
function getCommitSubject(message) {
    if (!message)
        return undefined;
    const [subject] = message.split("\n");
    return subject?.trim();
}
function formatCommitDate(value) {
    const date = new Date(value);
    if (Number.isNaN(date.getTime()))
        return value;
    return date.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
    });
}
